// src/components/portal/Sidebar.js
import React, { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Home, ShoppingCart, Repeat, LogOut, Utensils, Shield } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import logo from '../../assets/images/logo-chef.png';

const Sidebar = ({ sidebarOpen, setSidebarOpen, currentTab }) => {
  const trigger = useRef(null); // Ref for the toggle button (in Header)
  const sidebar = useRef(null); // Ref for the sidebar itself
  const { logout, userType } = useAuth();

  // Cerrar sidebar al hacer click fuera (en móvil)
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !sidebarOpen || window.innerWidth >= 1024) return;
      if (sidebar.current.contains(target) || (trigger.current && trigger.current.contains(target))) return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [sidebarOpen, setSidebarOpen, trigger]);

  // Cerrar sidebar con Esc
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [sidebarOpen, setSidebarOpen]);


  // Definición de enlaces (sin cambios)
  const allLinks = [
    { to: '/portal/resumen', icon: <Home size={24} />, text: 'Resumen', key: 'resumen', roles: ['student', 'parent', 'teacher'] },
    { to: '/portal/compras', icon: <ShoppingCart size={24} />, text: 'Historial de Compras', key: 'compras', roles: ['student', 'parent', 'teacher'] },
    { to: '/portal/recargas', icon: <Repeat size={24} />, text: 'Historial de Recargas', key: 'recargas', roles: ['student', 'parent', 'teacher'] },
    { to: '/portal/menu', icon: <Utensils size={24} />, text: 'Menú del Día', key: 'menu', roles: ['student', 'parent', 'teacher'] },
    { to: '/portal/restricciones', icon: <Shield size={24} />, text: 'Restringir Productos', key: 'restricciones', roles: ['parent', 'student'] },
  ];
  const links = allLinks.filter(link => userType && link.roles.includes(userType));

  return (
    <div
      ref={sidebar}
      // --- INICIO DE CORRECCIÓN (Altura y Flex) ---
      // 1. Cambiamos h-screen por h-dvh (altura dinámica de viewport)
      // 2. Quitamos h-screen y usamos h-dvh
      className={`fixed inset-y-0 left-0 z-40 flex h-dvh w-64 flex-col bg-gray-800
      // --- FIN DE CORRECCIÓN ---
        transition-transform duration-300 ease-in-out
        lg:static lg:w-auto lg:translate-x-0 lg:transition-none
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:${sidebarOpen ? 'lg:w-64' : 'lg:w-20'}
      `}
    >
      {/* Logo Area (No crece, no se encoge) */}
      <div className={`flex flex-shrink-0 items-center px-4 py-5 ${sidebarOpen ? 'lg:justify-start justify-center' : 'justify-center'}`}>
        <NavLink to="/portal/resumen" className="flex items-center">
          <img src={logo} alt="Logo" className="h-10 w-10 flex-shrink-0 rounded-full" />
           <span
             className={`ml-3 overflow-hidden whitespace-nowrap text-lg font-bold text-white transition-opacity duration-200
             ${sidebarOpen ? 'opacity-100' : 'opacity-0'}
             lg:${sidebarOpen ? 'lg:opacity-100' : 'lg:opacity-0'} `}
           >
             Banquetes
          </span>
        </NavLink>
      </div>

      {/* --- INICIO DE CORRECCIÓN (Scroll Interno) --- */}
      {/* Contenedor de enlaces: Crece (flex-1) y permite scroll (overflow-y-auto) */}
      {/* Esto asegura que los enlaces hagan scroll si no caben, pero el logo y el logout queden fijos */}
      <div className="flex flex-1 flex-col overflow-y-auto">
        <nav className="mt-5 flex-col px-4">
          {links.map(item => {
            const isActive = currentTab === item.key;
            return (
              <NavLink
                key={item.text}
                to={item.to}
                className={`group mb-2 flex items-center rounded-lg p-3 text-sm font-medium
                  text-gray-300 transition-colors hover:bg-gray-700 hover:text-white
                  ${isActive ? 'bg-gray-900 text-white' : ''}
                  ${sidebarOpen ? '' : 'justify-center'}
                `}
                onClick={() => { if (window.innerWidth < 1024) setSidebarOpen(false); }}
              >
                <div className="flex-shrink-0">{item.icon}</div>
                <span className={`ml-3 flex-1 overflow-hidden whitespace-nowrap transition-opacity duration-200
                    ${sidebarOpen ? 'opacity-100' : 'opacity-0'}
                    lg:${sidebarOpen ? 'lg:opacity-100' : 'lg:opacity-0'}
                `}>
                  {item.text}
                </span>
              </NavLink>
            );
          })}
        </nav>
      </div>
      {/* --- FIN DE CORRECCIÓN --- */}


      {/* Logout (No crece, no se encoge, siempre visible abajo) */}
      <div className="flex-shrink-0 p-4">
        <button
          onClick={logout}
          className={`group flex w-full items-center rounded-lg p-3 text-sm font-medium
          text-gray-300 transition-colors hover:bg-gray-700 hover:text-white
          ${sidebarOpen ? '' : 'justify-center'}`}
        >
          <LogOut size={24} className="flex-shrink-0"/>
           <span className={`ml-3 flex-1 overflow-hidden whitespace-nowrap transition-opacity duration-200
               ${sidebarOpen ? 'opacity-100' : 'opacity-0'}
               lg:${sidebarOpen ? 'lg:opacity-100' : 'lg:opacity-0'}
           `}>
             Cerrar Sesión
           </span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;